import React, { useEffect, useState, useMemo, useRef } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState, GetActiveStadiumZones } from './../../../utilities/BL/Zones';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import { Prepare as BLPrepare, Start as BLStart, Stop as BLStop, GetAllPresets, CreatePreset, UpdatePreset, DeletePreset, GetLightShowStats, GetSettings as BLGetSettings, SaveSettings as BLSaveSettings, ApplySettings as BLApplySettings } from './../../../utilities/BL/Admin/Games/LightShow';
import { Confirm } from './../../Commons/Modals/Confirm';
import { ValidateSqlInjection } from './../../../utilities/BL/Security';
import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';

const LightShow = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // ==========================================
    // 1. ESTADOS PERSISTIDOS EN BASE DE DATOS Y LOCALSTORAGE
    // ==========================================
    const [beatsPerMinute, setBeatsPerMinute] = useState(() => {
        return parseInt(localStorage.getItem('lightshow_bpm')) || 118;
    });
    const [themeColor, setThemeColor] = useState(() => {
        return localStorage.getItem('lightshow_globalPulseColor') || '#FFFFFF';
    });
    const [sweepSpeed, setSweepSpeed] = useState(() => {
        return parseFloat(localStorage.getItem('lightshow_sweepSpeed')) || 1.5;
    });
    const [globalSelectedPresetId, setGlobalSelectedPresetId] = useState(() => {
        const val = localStorage.getItem('lightshow_globalPresetId');
        return val ? parseInt(val) : '';
    });
    const [sweepSelectedPresetId, setSweepSelectedPresetId] = useState(() => {
        const val = localStorage.getItem('lightshow_sweepPresetId');
        return val ? parseInt(val) : '';
    });
    const [zoneSelectedPreset, setZoneSelectedPreset] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('lightshow_zoneSelectedPreset')) || {};
        } catch (e) { return {}; }
    });
    const [zonePulseColor, setZonePulseColor] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('lightshow_zonePulseColor')) || {};
        } catch (e) { return {}; }
    });

    // Nuevos estados para control en vivo y color de apagado
    const [flashActive, setFlashActive] = useState(() => {
        const val = localStorage.getItem('lightshow_flashActive');
        return val !== 'false'; 
    });
    const [screenActive, setScreenActive] = useState(() => {
        const val = localStorage.getItem('lightshow_screenActive');
        return val !== 'false'; 
    });
    const [offColor, setOffColor] = useState(() => {
        return localStorage.getItem('lightshow_offColor') || '#000000';
    });
    const [zoneOrder, setZoneOrder] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('lightshow_zoneOrder')) || [];
        } catch (e) { return []; }
    });
    const [dbZoneOrder, setDbZoneOrder] = useState([]);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const activeSortedZonesRef = useRef([]);

    const isLoadedRef = useRef(false);

    // Guardar cambios en DB y LocalStorage automáticamente con debounce
    useEffect(() => {
        if (!isLoadedRef.current) return;

        const timer = setTimeout(async () => {
            const settingsDto = {
                beatsPerMinute,
                themeColor,
                sweepSpeed,
                globalSelectedPresetId: globalSelectedPresetId || null,
                sweepSelectedPresetId: sweepSelectedPresetId || null,
                zoneSelectedPresetJson: JSON.stringify(zoneSelectedPreset),
                zonePulseColorJson: JSON.stringify(zonePulseColor),
                zoneOrderJson: JSON.stringify(zoneOrder),
                flashActive,
                screenActive,
                offColor
            };

            await BLSaveSettings(settingsDto);

            localStorage.setItem('lightshow_bpm', beatsPerMinute);
            localStorage.setItem('lightshow_globalPulseColor', themeColor);
            localStorage.setItem('lightshow_sweepSpeed', sweepSpeed);
            localStorage.setItem('lightshow_globalPresetId', globalSelectedPresetId);
            localStorage.setItem('lightshow_sweepPresetId', sweepSelectedPresetId);
            localStorage.setItem('lightshow_zoneSelectedPreset', JSON.stringify(zoneSelectedPreset));
            localStorage.setItem('lightshow_zonePulseColor', JSON.stringify(zonePulseColor));
            localStorage.setItem('lightshow_zoneOrder', JSON.stringify(zoneOrder));
            localStorage.setItem('lightshow_flashActive', flashActive);
            localStorage.setItem('lightshow_screenActive', screenActive);
            localStorage.setItem('lightshow_offColor', offColor);
        }, 800);

        return () => clearTimeout(timer);
    }, [
        beatsPerMinute, themeColor, sweepSpeed, globalSelectedPresetId,
        sweepSelectedPresetId, zoneSelectedPreset, zonePulseColor, zoneOrder,
        flashActive, screenActive, offColor
    ]);

    // ==========================================
    // 2. ESTADOS DEL DISEÑADOR DE PRESETS
    // ==========================================
    const [animationFrames, setAnimationFrames] = useState([
        { id: 1, color: '#d6352d', flashOn: true }, 
        { id: 2, color: '#000000', flashOn: false }
    ]);
    const [activePreset, setActivePreset] = useState(null);
    const [presetsDb, setPresetsDb] = useState([]);
    const [isLoadingPresets, setIsLoadingPresets] = useState(true);
    const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);

    // ==========================================
    // 3. ESTADOS DE OPERACIÓN Y RED
    // ==========================================
    const [currentSystemStatus, setCurrentSystemStatus] = useState('Esperando comandos.');
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [isAutoAssignment, setIsAutoAssignment] = useState(false); // Manual por defecto
    const [stadiumZones, setStadiumZones] = useState([]);
    
    // Estadísticas
    const [liveStats, setLiveStats] = useState(null);
    const [lightShowStats, setLightShowStats] = useState([]);

    // ==========================================
    // 4. ESTADOS DE BÚSQUEDA, FILTRADO Y ORDEN
    // ==========================================
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); 
    const [sortBy, setSortBy] = useState('name'); 

    // ==========================================
    // 5. SECUENCIA DE BARRIDO AUTOMÁTICO (OLA)
    // ==========================================
    const [isSweeping, setIsSweeping] = useState(false);
    const sweepIntervalRef = useRef(null);
    const sweepIndexRef = useRef(0);

    // ==========================================
    // 6. COLAS Y PROMESAS DE BLOQUEO (PREVIENE ENCIENDES Y APAGAS TRABADOS)
    // ==========================================
    const pendingPulsePromiseRef = useRef(null);

    // Modales de Confirmación
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const triggerModal = (msg, action, btnText) => {
        setConfirmModalData({ message: msg, actionToYesButton: action, textYesButton: btnText, textNoButton: 'Cancelar' });
        setShowConfirmModal(true);
    };

    // ==========================================
    // 7. CARGA DE DATOS INICIAL
    // ==========================================
    const loadPresetsFromDb = async () => {
        setIsLoadingPresets(true);
        const res = await GetAllPresets();
        if (res.success) {
            setPresetsDb(res.data);
            if (!activePreset && res.data.length > 0) {
                applyPresetToUI(res.data[0]);
            }
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
        setIsLoadingPresets(false);
    };

    const loadStadiumZones = async () => {
        const res = await GetActiveStadiumZones();
        if (res.success && res.data.length > 0) {
            setStadiumZones(res.data);
            // Autocompletar colores e inicializaciones si faltan
            setZonePulseColor(prev => {
                const copy = { ...prev };
                res.data.forEach(z => {
                    if (!copy[z.internalName]) copy[z.internalName] = '#FFFFFF';
                });
                return copy;
            });
        } else {
            // FALLBACK DE SEGURIDAD
            // =========================================================================
            // PARA PRUEBAS (6 ZONAS):
            const defaultZones = [
                { displayName: "Zona 1", internalName: "zona1" },
                { displayName: "Platea Zona 1", internalName: "zona2" },
                { displayName: "Palcos Zona 1", internalName: "zona3" },
                { displayName: "Cabecera Norte", internalName: "zona4" },
                { displayName: "Cabecera Sur", internalName: "zona5" },
                { displayName: "Zona 2", internalName: "zona6" },
                { displayName: "Platea Zona 2", internalName: "zona7" },
                { displayName: "Palcos Zona 2", internalName: "zona8" }
            ];

            // PARA PRODUCCIÓN (2 ZONAS ORIGINALES):
            // const defaultZones = [
            //     { displayName: "Xolo Mayor Rojo", internalName: "zona1" },
            //     { displayName: "Xolo Mayor Blanco", internalName: "zona2" }
            // ];
            // =========================================================================
            setStadiumZones(defaultZones);
            setZonePulseColor(prev => {
                const copy = { ...prev };
                defaultZones.forEach(z => {
                    if (!copy[z.internalName]) copy[z.internalName] = '#FFFFFF';
                });
                return copy;
            });
        }
    };

    const loadSettingsFromDb = async () => {
        const res = await BLGetSettings();
        if (res.success && res.data) {
            const s = res.data;
            setBeatsPerMinute(s.beatsPerMinute);
            setThemeColor(s.themeColor);
            setSweepSpeed(s.sweepSpeed);
            setGlobalSelectedPresetId(s.globalSelectedPresetId ?? '');
            setSweepSelectedPresetId(s.sweepSelectedPresetId ?? '');
            setFlashActive(s.flashActive);
            setScreenActive(s.screenActive);
            setOffColor(s.offColor);
            try {
                setZoneSelectedPreset(JSON.parse(s.zoneSelectedPresetJson) || {});
            } catch (e) {}
            try {
                setZonePulseColor(JSON.parse(s.zonePulseColorJson) || {});
            } catch (e) {}
            try {
                const orderList = JSON.parse(s.zoneOrderJson);
                if (Array.isArray(orderList)) {
                    setDbZoneOrder(orderList);
                }
            } catch (e) {}
        }
    };

    // Efecto para inicializar y fusionar zoneOrder con stadiumZones
    useEffect(() => {
        if (stadiumZones.length === 0) return;

        let mergedOrder = [...dbZoneOrder];
        const existingZoneNames = new Set(stadiumZones.map(z => z.internalName));
        
        // Mantener solo zonas que aún existan
        mergedOrder = mergedOrder.filter(name => existingZoneNames.has(name));
        
        // Agregar nuevas zonas al final
        stadiumZones.forEach(z => {
            if (!mergedOrder.includes(z.internalName)) {
                mergedOrder.push(z.internalName);
            }
        });
        
        setZoneOrder(mergedOrder);
    }, [stadiumZones, dbZoneOrder]);


    useEffect(() => {
        const init = async () => {
            await loadPresetsFromDb();
            await loadStadiumZones();
            await loadSettingsFromDb();
            isLoadedRef.current = true;
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Polling de estadísticas
    useEffect(() => {
        const fetchAllStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
                if (response.ok) {
                    const statsData = await response.json();
                    setLiveStats(statsData);
                }

                const lsRes = await GetLightShowStats();
                if (lsRes.success) {
                    setLightShowStats(lsRes.data);
                }

                const autoRes = await fetch(`${process.env.REACT_APP_API_URL}ZonesAssignment/stats`);
                if (autoRes.ok) {
                    const data = await autoRes.json();
                    setIsAutoAssignment(data.isAutoAssignmentActive);
                }
            } catch (error) { }
        };

        fetchAllStats();
        const interval = setInterval(fetchAllStats, 2000);
        return () => clearInterval(interval);
    }, []);

    // Cambios sin guardar en diseñador
    const hasUnsavedChanges = useMemo(() => {
        if (!activePreset) return true;
        if (activePreset.beatsPerMinute !== beatsPerMinute) return true;
        try {
            const currentSimple = animationFrames.map(f => ({ c: f.color.toLowerCase(), f: f.flashOn }));
            const savedDbFrames = JSON.parse(activePreset.framesJson);
            const savedSimple = savedDbFrames.map(f => ({ c: (f.color || '').toLowerCase(), f: f.flashOn }));
            return JSON.stringify(currentSimple) !== JSON.stringify(savedSimple);
        } catch (e) { return true; }
    }, [activePreset, beatsPerMinute, animationFrames]);

    // ==========================================
    // 8. LOGICA DE EXCLUSIÓN MUTUA (DESPINTADO PREVIO)
    // ==========================================
    const stopAllOngoingRhythmsAndSweeps = () => {
        // Detener Ola si está corriendo
        if (sweepIntervalRef.current) {
            clearInterval(sweepIntervalRef.current);
            sweepIntervalRef.current = null;
        }
        setIsSweeping(false);
    };

    // ==========================================
    // 9. METODOS DEL DISEÑADOR (PRESETS)
    // ==========================================
    const applyPresetToUI = (preset) => {
        try {
            const parsedFrames = JSON.parse(preset.framesJson);
            setAnimationFrames(parsedFrames);
            setBeatsPerMinute(preset.beatsPerMinute);
            setActivePreset(preset);
        } catch (e) { 
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al decodificar colores del preset.' }); 
        }
    };

    const handleSaveUpdate = async () => {
        if (!activePreset) return;
        const res = await UpdatePreset(activePreset.id, activePreset.name, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            setActivePreset(prev => ({ ...prev, beatsPerMinute, framesJson: JSON.stringify(animationFrames) }));
            await loadPresetsFromDb(); 
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
    };

    const handleSaveAsNew = async () => {
        const newName = window.prompt("Escribe el nombre de tu nuevo preset:");
        if (!newName || newName.trim() === '') return;
        const isMalicious = await ValidateSqlInjection(newName);
        if (isMalicious) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Evita caracteres especiales.' });
            return;
        }
        const res = await CreatePreset(newName, beatsPerMinute, animationFrames);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            await loadPresetsFromDb();
            applyPresetToUI(res.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
    };

    const handleDeletePreset = async () => {
        if (!activePreset) return;
        if (activePreset.isSystemPreset) return;
        const res = await DeletePreset(activePreset.id);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: res.message });
            setActivePreset(null);
            await loadPresetsFromDb();
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
        }
    };

    const handleRestoreDefault = () => {
        if (activePreset) applyPresetToUI(activePreset);
    };

    // Funciones del diseñador de frames
    const addFrame = () => {
        const lastFrame = animationFrames.length > 0 ? animationFrames[animationFrames.length - 1] : null;
        const newFrame = { id: Date.now(), color: '#d6352d', flashOn: lastFrame ? !lastFrame.flashOn : true };
        setAnimationFrames([...animationFrames, newFrame]);
    };

    const removeFrame = (frameId) => {
        if (animationFrames.length <= 1) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes tener al menos 1 frame.' });
        setAnimationFrames(animationFrames.filter(frame => frame.id !== frameId));
    };

    const updateFrame = (id, prop, val) => setAnimationFrames(prev => prev.map(f => f.id === id ? { ...f, [prop]: val } : f));
    
    const moveFrameUp = (i) => { 
        if (i === 0) return; 
        const arr = [...animationFrames]; 
        [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; 
        setAnimationFrames(arr); 
    };
    
    const moveFrameDown = (i) => { 
        if (i === animationFrames.length - 1) return; 
        const arr = [...animationFrames]; 
        [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; 
        setAnimationFrames(arr); 
    };

    // ==========================================
    // 10. OPERACIONES GLOBALES (HEADER / MANDOS)
    // ==========================================
    const toggleAutozoneMode = async (enabled) => {
        try {
            setIsSystemWorking(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}ZonesAssignment/toggleAutozone?enabled=${enabled}`, {
                method: 'POST'
            });
            if (response.ok) {
                setIsAutoAssignment(enabled);
                showMessage({ 
                    type: MESSAGE_TYPE.SUCCESS, 
                    text: `Modo de Asignación ${enabled ? 'AUTOMÁTICA' : 'MANUAL'} activado.` 
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar modo de asignación.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error en la petición de red.' });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handlePrepareDynamicWelcome = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Preparando nueva sala de arena..." });
            await BLResetCurrentDynamic(); 
            // Crear nueva ronda
            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: 'Show de Luces' })
            });
            await SetScreenV2MainDynamic(1, null);
            setCurrentSystemStatus('Nueva sala inicializada con éxito.');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Sala inicializada." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de red al inicializar." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleShowQRWelcome = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Enviando código QR a la pantalla gigante..." });
            await fetch(`${process.env.REACT_APP_API_URL}lightshow/sendWelcome`, { method: 'POST' });
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "QR enviado a la pantalla gigante." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al enviar QR." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handlePrepareAllDevices = async () => {
        // Buscamos cuál es el preset/patrón global elegido
        let bpm = beatsPerMinute;
        let frames = animationFrames;
        let pName = activePreset ? activePreset.name : "Personalizado";

        if (globalSelectedPresetId) {
            const selected = presetsDb.find(p => p.id === globalSelectedPresetId);
            if (selected) {
                bpm = selected.beatsPerMinute;
                frames = JSON.parse(selected.framesJson);
                pName = selected.name;
            }
        }

        setCurrentSystemStatus('Enviando solicitud de permisos (Lobby) a toda la arena...');
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Enviando petición de permisos...' });
        const prepResponse = await BLPrepare(bpm, frames, null, pName, flashActive, screenActive, offColor);
        if (prepResponse?.success) {
            setCurrentSystemStatus('Fase de Permisos: Esperando que los usuarios autoricen.');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Permisos solicitados globalmente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: prepResponse?.message || 'Error.' });
        }
    };

    const handleStartShowGlobal = async () => {
        // DETENER CUALQUIER ESTADO PREVIO (Exclusión mutua)
        stopAllOngoingRhythmsAndSweeps();

        // Determinar qué patrón global se ejecutará
        let bpm = beatsPerMinute;
        let frames = animationFrames;
        let pName = activePreset ? activePreset.name : "Personalizado";

        if (globalSelectedPresetId) {
            const selected = presetsDb.find(p => p.id === globalSelectedPresetId);
            if (selected) {
                bpm = selected.beatsPerMinute;
                frames = JSON.parse(selected.framesJson);
                pName = selected.name;
            }
        }

        setCurrentSystemStatus('Iniciando show global en toda la arena...');
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Enviando señal de encendido global...' });
        const startResponse = await BLStart(bpm, frames, null, pName, flashActive, screenActive, offColor);
        
        if (startResponse?.success) {
            setCurrentSystemStatus(`¡SHOW ACTIVO GLOBAL! 🌟 (${pName} - ${bpm} BPM)`);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces encendidas globalmente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse?.message || 'Error.' });
        }
    };

    const handleStopShowGlobal = async () => {
        // DETENER CUALQUIER ESTADO PREVIO
        stopAllOngoingRhythmsAndSweeps();

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Apagando luces globalmente...' });
        // Apagar todos los dispositivos (global)
        const response = await BLStop(null);
        if (response?.success) {
            setCurrentSystemStatus('Show global y local detenido 🛑');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Luces apagadas en toda la arena.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'Error.' });
        }
    };

    const handleApplySettings = async () => {
        try {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Aplicando configuración en vivo...' });
            const settingsDto = {
                flashActive,
                screenActive,
                offColor
            };
            const response = await BLApplySettings(settingsDto);
            if (response?.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración aplicada a la sala.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'Error al aplicar.' });
            }
        } catch (error) {
            console.error("Error al aplicar la configuración:", error);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Excepción al aplicar configuración.' });
        }
    };

    // PULSO GLOBAL: Hold-to-Flash con control secuencial (Evita apagados trabados)
    const handlePulseGlobalStart = async () => {
        stopAllOngoingRhythmsAndSweeps();
        const pulseFrame = [{ color: themeColor, flashOn: true }];
        
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Enviando pulso global...' });
        const promise = BLStart(60, pulseFrame, null, "Pulso Global", flashActive, screenActive, offColor);
        pendingPulsePromiseRef.current = promise;
        await promise;
    };

    const handlePulseGlobalStop = async () => {
        if (pendingPulsePromiseRef.current) {
            await pendingPulsePromiseRef.current;
            pendingPulsePromiseRef.current = null;
        }
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Apagando pulso global...' });
        await BLStop(null);
    };

    // ==========================================
    // 11. OPERACIONES POR ZONA (TARJETAS)
    // ==========================================
    
    // ACCIÓN 1: Iniciar X zona sin detener las demás zonas running (pero sí detiene la ola)
    const handleStartZoneNonExclusive = async (zoneName) => {
        // Detener Ola
        if (isSweeping) {
            if (sweepIntervalRef.current) {
                clearInterval(sweepIntervalRef.current);
                sweepIntervalRef.current = null;
            }
            setIsSweeping(false);
        }

        let bpm = beatsPerMinute;
        let frames = animationFrames;
        let pName = activePreset ? activePreset.name : "Personalizado";

        const presetId = zoneSelectedPreset[zoneName];
        if (presetId) {
            const selected = presetsDb.find(p => p.id === presetId);
            if (selected) {
                bpm = selected.beatsPerMinute;
                frames = JSON.parse(selected.framesJson);
                pName = selected.name;
            }
        }

        showMessage({ type: MESSAGE_TYPE.INFO, text: `Enviando señal de encendido a Zona ${zoneName.toUpperCase()}...` });
        const res = await BLStart(bpm, frames, [zoneName], pName, flashActive, screenActive, offColor);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Zona ${zoneName.toUpperCase()} encendida.` });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error.' });
        }
    };

    // ACCIÓN 2: Iniciar X zona con EXCLUSIÓN (Detiene el show global y todas las demás zonas)
    const handleStartZoneExclusive = async (zoneName) => {
        // Detener todo lo demás primero
        stopAllOngoingRhythmsAndSweeps();
        
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Apagando otras zonas e iniciando Zona ${zoneName.toUpperCase()}...` });
        // Apagamos todas las demás zonas
        const otherZones = stadiumZones.filter(z => z.internalName !== zoneName).map(z => z.internalName);
        if (otherZones.length > 0) {
            await BLStop(otherZones);
        }

        let bpm = beatsPerMinute;
        let frames = animationFrames;
        let pName = activePreset ? activePreset.name : "Personalizado";

        const presetId = zoneSelectedPreset[zoneName];
        if (presetId) {
            const selected = presetsDb.find(p => p.id === presetId);
            if (selected) {
                bpm = selected.beatsPerMinute;
                frames = JSON.parse(selected.framesJson);
                pName = selected.name;
            }
        }

        const res = await BLStart(bpm, frames, [zoneName], pName, flashActive, screenActive, offColor);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Exclusivo: Solo Zona ${zoneName.toUpperCase()} activa.` });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error.' });
        }
    };

    // ACCIÓN 3: Detener X zona
    const handleStopZone = async (zoneName) => {
        if (isSweeping) stopDynamicSweep();
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Apagando Zona ${zoneName.toUpperCase()}...` });
        const res = await BLStop([zoneName]);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Zona ${zoneName.toUpperCase()} detenida.` });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error.' });
        }
    };

    // PULSO POR ZONA: Hold-to-Flash individual con control secuencial (Evita apagados trabados)
    const handlePulseZoneStart = async (zoneName) => {
        stopAllOngoingRhythmsAndSweeps();
        const color = zonePulseColor[zoneName] || '#FFFFFF';
        const pulseFrame = [{ color: color, flashOn: true }];
        
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Enviando pulso a Zona ${zoneName.toUpperCase()}...` });
        const promise = BLStart(60, pulseFrame, [zoneName], "Pulso Fijo", flashActive, screenActive, offColor);
        pendingPulsePromiseRef.current = promise;
        await promise;
    };

    const handlePulseZoneStop = async (zoneName) => {
        if (pendingPulsePromiseRef.current) {
            await pendingPulsePromiseRef.current;
            pendingPulsePromiseRef.current = null;
        }
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Apagando pulso de Zona ${zoneName.toUpperCase()}...` });
        await BLStop([zoneName]);
    };

    // ==========================================
    // 12. SECUENCIA DE OLA (BARRIDO AUTOMÁTICO)
    // ==========================================
    const startDynamicSweep = () => {
        if (stadiumZones.length < 2) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Se requieren mínimo 2 zonas para el barrido.' });
            return;
        }
        if (isSweeping) return;

        const activeSorted = activeSortedZonesRef.current;
        if (activeSorted.length === 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'No hay dispositivos activos en ninguna zona para iniciar la oleada.' });
            return;
        }

        // DETENER OTROS ESTADOS
        stopAllOngoingRhythmsAndSweeps();

        setIsSweeping(true);
        sweepIndexRef.current = 0;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciando Ola secuencial...' });
        setCurrentSystemStatus('Oleada activa: Secuencia rotativa.');

        const runStep = async () => {
            const currentActiveSorted = activeSortedZonesRef.current;
            if (currentActiveSorted.length === 0) return;

            const index = sweepIndexRef.current;
            const currentActiveZoneName = currentActiveSorted[index % currentActiveSorted.length];
            const currentActiveZone = stadiumZones.find(z => z.internalName === currentActiveZoneName);
            if (!currentActiveZone) {
                sweepIndexRef.current++;
                return;
            }

            // Determinar si la Ola corre un Preset o un Color Fijo
            let bpm = 60;
            let frames = [{ color: themeColor, flashOn: true }]; // Por defecto color global
            let pName = "Ola Barrido";

            if (sweepSelectedPresetId) {
                const selected = presetsDb.find(p => p.id === sweepSelectedPresetId);
                if (selected) {
                    bpm = selected.beatsPerMinute;
                    frames = JSON.parse(selected.framesJson);
                    pName = `Ola: ${selected.name}`;
                }
            }

            // Encendemos esta zona
            showMessage({ type: MESSAGE_TYPE.INFO, text: `Ola: Encendiendo Zona ${currentActiveZone.displayName}...` });
            await BLStart(bpm, frames, [currentActiveZone.internalName], pName, flashActive, screenActive, offColor);

            // Apagamos todas las demás zonas activas
            const otherZones = currentActiveSorted.filter(name => name !== currentActiveZone.internalName);

            if (otherZones.length > 0) {
                await BLStop(otherZones);
            }

            sweepIndexRef.current++;
        };

        runStep();
        sweepIntervalRef.current = setInterval(runStep, sweepSpeed * 1000);
    };

    const stopDynamicSweep = () => {
        if (sweepIntervalRef.current) {
            clearInterval(sweepIntervalRef.current);
            sweepIntervalRef.current = null;
        }
        setIsSweeping(false);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Deteniendo oleada de luces...' });
        BLStop(null);
        setCurrentSystemStatus('Secuencia de barrido detenida.');
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ola de luces apagada.' });
    };

    // Retorno al panel principal
    const ReturnToDashboard = async () => {
        if (isSweeping) stopDynamicSweep();
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    // ==========================================
    // FILTRADO Y ORDEN DE TARJETAS
    // ==========================================
    const mergedZoneStats = useMemo(() => {
        if (!stadiumZones) return [];
        return stadiumZones.map(sz => {
            const zs = (liveStats && liveStats.zoneStats && liveStats.zoneStats.find(s => s.zone.toLowerCase() === sz.internalName.toLowerCase())) || {
                currentActive: 0,
                totalEntered: 0,
                totalLeft: 0
            };
            const ls = lightShowStats.find(s => s.zone.toLowerCase() === sz.internalName.toLowerCase()) || {
                isRunning: false,
                activePresetName: 'Ninguno',
                activeColor: 'N/A'
            };

            return {
                zoneInternal: sz.internalName,
                displayName: sz.displayName,
                active: zs.currentActive,
                total: zs.totalEntered,
                left: zs.totalLeft,
                isRunning: ls.isRunning,
                activePreset: ls.activePresetName,
                activeColor: ls.activeColor
            };
        });
    }, [liveStats, lightShowStats, stadiumZones]);

    // Efecto para mantener al día el ref de las zonas activas ordenadas
    useEffect(() => {
        const activeZonesSet = new Set(
            mergedZoneStats.filter(z => z.active > 0).map(z => z.zoneInternal)
        );
        const activeSorted = zoneOrder.filter(name => activeZonesSet.has(name));
        activeSortedZonesRef.current = activeSorted;
    }, [mergedZoneStats, zoneOrder]);

    const processedZones = useMemo(() => {
        let result = [...mergedZoneStats];

        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            result = result.filter(z => 
                z.displayName.toLowerCase().includes(term) || 
                z.zoneInternal.toLowerCase().includes(term)
            );
        }

        if (filterType === 'active') {
            result = result.filter(z => z.isRunning);
        } else if (filterType === 'inactive') {
            result = result.filter(z => !z.isRunning);
        }

        result.sort((a, b) => {
            if (sortBy === 'name') {
                return a.displayName.localeCompare(b.displayName);
            } else if (sortBy === 'connections') {
                return b.active - a.active; 
            } else if (sortBy === 'status') {
                return (b.isRunning ? 1 : 0) - (a.isRunning ? 1 : 0); 
            }
            return 0;
        });

        return result;
    }, [mergedZoneStats, searchTerm, filterType, sortBy]);

    const getCardStyle = (stat) => {
        if (stat.isRunning) {
            const color = stat.activeColor !== 'N/A' ? stat.activeColor : '#d6352d';
            return {
                border: `2px solid ${color}`,
                boxShadow: `0 0 15px ${color}55`,
                //backgroundColor: '#1E293B',
                transition: 'all 0.3s ease',
                minWidth: '290px',
                maxWidth: '320px',
                borderRadius: '16px'
            };
        }
        return {
            border: '1px solid rgba(255,255,255,0.08)',
            //backgroundColor: '#0F172A',
            transition: 'all 0.3s ease',
            minWidth: '290px',
            maxWidth: '320px',
            borderRadius: '16px'
        };
    };


    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5' style={{ color: '#000', borderRadius: '15px', padding: '20px' }}>
                
                {/* ENCABEZADO SUPERIOR */}
                <div className="d-flex justify-content-between align-items-center border-bottom border-secondary pb-3 mb-4">
                    <p className='AdmRunToGoal__Title mb-0 fw-bold fs-3'>
                        Consola de Luces Pro v2
                    </p>
                    <div className="d-flex align-items-center gap-3">
                        <span className="small text-muted">Status: <strong className="text-warning">{currentSystemStatus}</strong></span>
                        <button className="btn btn-primary fw-bold shadow-sm" onClick={() => setIsPresetModalOpen(true)}>
                            Diseñador de presets
                        </button>
                    </div>
                </div>

                {/* MODO DE ASIGNACIÓN */}
                <div className="text-center mb-3">
                    <span className="text-muted small me-2">Modo de asignación: <strong>{isAutoAssignment ? 'Automático' : 'Manual'}</strong></span>
                    <button className="btn btn-sm btn-outline-secondary p-0 border-0" onClick={() => toggleAutozoneMode(!isAutoAssignment)} title="Cambiar Modo">
                        <i className="bi bi-pencil-square text-info fs-6"></i>
                    </button>
                </div>

                {/* PANEL INICIALIZACIÓN (NUEVA SALA Y QR) */}
                <div className="d-flex flex-column flex-lg-row justify-content-center align-items-center gap-2 mb-4">
                    {/* <button className="btn btn-outline-success fw-bold px-5 py-2 shadow-sm rounded-pill" onClick={handlePrepareDynamicWelcome} style={{ minWidth: '280px' }}>
                        Nueva sala
                    </button> */}
                    <button className="btn btn-outline-success fw-bold px-5 py-2 shadow-sm rounded-pill" onClick={handlePrepareAllDevices} style={{ minWidth: '280px' }}>
                        Enviar petición de permisos
                    </button>
                    <button className="btn btn-outline-success fw-bold px-5 py-2 shadow-sm rounded-pill" onClick={handleShowQRWelcome} style={{ minWidth: '280px' }}>
                        Mostrar código QR
                    </button>
                </div>

                {/* CONFIGURACIÓN DE DISPOSITIVOS (LUZ Y PANTALLA) */}
                <div className="card border-secondary border-opacity-40 mb-4 rounded-4" style={{ backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.1)' }}>
                    <div className="card-header p-2 border-secondary border-opacity-40 fw-bold small text-uppercase text-center text-dark" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                        Configuración de Dispositivos (Luz y Pantalla)
                    </div>
                    <div className="card-body py-3 px-3 text-dark">
                        <div className="d-flex flex-wrap justify-content-around align-items-center gap-3">
                            {/* Control Flash */}
                            <div className="form-check form-switch d-flex align-items-center gap-2">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="flashActiveSwitch"
                                    checked={flashActive} 
                                    onChange={(e) => setFlashActive(e.target.checked)} 
                                    style={{ cursor: 'pointer', width: '45px', height: '22px' }}
                                />
                                <label className="form-check-label fw-bold mb-0" htmlFor="flashActiveSwitch" style={{ cursor: 'pointer' }}>
                                    Flash en Celulares (Activo)
                                </label>
                            </div>

                            {/* Control Pantalla */}
                            <div className="form-check form-switch d-flex align-items-center gap-2">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="screenActiveSwitch"
                                    checked={screenActive} 
                                    onChange={(e) => setScreenActive(e.target.checked)} 
                                    style={{ cursor: 'pointer', width: '45px', height: '22px' }}
                                />
                                <label className="form-check-label fw-bold mb-0" htmlFor="screenActiveSwitch" style={{ cursor: 'pointer' }}>
                                    Color en Pantallas (Activo)
                                </label>
                            </div>

                            {/* Control Color de Apagado */}
                            <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold small text-muted">Color de apagado/lobby:</span>
                                <input 
                                    type="color" 
                                    className="form-control-color border-0 p-0" 
                                    value={offColor} 
                                    onChange={(e) => setOffColor(e.target.value)} 
                                    style={{ width: '45px', height: '34px', cursor: 'pointer', borderRadius: '6px' }} 
                                />
                                <span className="font-monospace fw-bold small text-dark">{offColor.toUpperCase()}</span>
                            </div>

                            {/* Botón Aplicar en Vivo */}
                            <div className="mt-3 pt-2 border-top border-secondary border-opacity-10 d-flex justify-content-start">
                                <button className="btn btn-sm btn-primary fw-bold px-3 py-2 rounded-3 d-flex align-items-center gap-2" onClick={handleApplySettings}>
                                    <i className="bi bi-send-fill"></i> Aplicar Ajustes en Vivo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* APLICAR A TODAS LAS ZONAS (ESTILO CONSOLA) */}
                <div className="card border-secondary border-opacity-40 mb-4 rounded-4 text-center">
                    <div className="card-header p-2 border-secondary border-opacity-40 fw-bold small text-uppercase">
                        Aplicar a todas las zonas
                    </div>
                    <div className="card-body py-4 px-3">
                        <div className="row align-items-stretch">
                            {/* COLUMNA IZQUIERDA: ENCENDER / APAGAR GENERAL */}
                            <div className="col-md-5 d-flex flex-column justify-content-center align-items-center gap-3">
                                {/* Selector del preset a encender */}
                                <div className="w-100" style={{ maxWidth: '240px' }}>
                                    <label className="small text-muted fw-bold d-block mb-1">Patrón a encender:</label>
                                    <select className="form-select form-select-sm border-secondary border-opacity-50" value={globalSelectedPresetId} onChange={(e) => setGlobalSelectedPresetId(parseInt(e.target.value) || '')}>
                                        <option value="">(Diseñador Actual / Personalizado)</option>
                                        {presetsDb.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <button className="btn btn-outline-success btn-lg fw-bold py-2 rounded-3" onClick={handleStartShowGlobal} style={{ width: '240px' }}>
                                    Encender todo
                                </button>
                                <button className="btn btn-outline-danger btn-lg fw-bold py-2 rounded-3" onClick={handleStopShowGlobal} style={{ width: '240px' }}>
                                    Apagar todo
                                </button>
                            </div>

                            {/* LÍNEA DIVISORIA VERTICAL */}
                            <div className="col-md-2 d-none d-md-flex justify-content-center">
                                <div className="border-start border-secondary border-opacity-40 h-100"></div>
                            </div>

                            {/* COLUMNA DERECHA: OLEADA Y PULSOS */}
                            <div className="col-md-5 d-flex flex-column justify-content-center align-items-center gap-3 mt-3 mt-md-0">
                                {/* Patrón para la Ola */}
                                <div className="w-100" style={{ maxWidth: '240px' }}>
                                    <label className="small text-muted fw-bold d-block mb-1">Patrón para la secuencia:</label>
                                    <select className="form-select form-select-sm border-secondary border-opacity-50" value={sweepSelectedPresetId} onChange={(e) => setSweepSelectedPresetId(parseInt(e.target.value) || '')}>
                                        <option value="">(Luz Fija Color de Pulso)</option>
                                        {presetsDb.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Botón de Activar Ola */}
                                {!isSweeping ? (
                                    <button 
                                        className="btn btn-outline-info btn-lg fw-bold py-2 rounded-3" 
                                        onClick={startDynamicSweep} 
                                        style={{ width: '240px' }}
                                        disabled={isAutoAssignment}
                                        title={isAutoAssignment ? "La secuencia no está disponible en modo automático" : ""}
                                    >
                                        Activar secuencia
                                    </button>
                                ) : (
                                    <button 
                                        className="btn btn-danger btn-lg fw-bold py-2 rounded-3 shadow animate-pulse" 
                                        onClick={stopDynamicSweep} 
                                        style={{ width: '240px' }}
                                    >
                                        Detener secuencia
                                    </button>
                                )}

                                {/* Botón para Configurar Orden de Ola */}
                                <button 
                                    className="btn btn-outline-secondary btn-sm fw-bold py-1.5 rounded-3" 
                                    onClick={() => setIsOrderModalOpen(true)} 
                                    style={{ width: '240px' }}
                                    disabled={isAutoAssignment}
                                    title={isAutoAssignment ? "La configuración no está disponible en modo automático" : ""}
                                >
                                    <i className="bi bi-sliders me-1"></i> Configurar orden de secuencia
                                </button>

                                {isAutoAssignment && (
                                    <span className="text-danger small fw-bold text-center" style={{ maxWidth: '240px' }}>
                                        ⚠️ Modo Automático activo: Secuencia deshabilitada.
                                    </span>
                                )}

                                {/* Botones rápidos de velocidad */}
                                <div className="d-flex gap-1 justify-content-center" style={{ width: '240px' }}>
                                    {[0.2, 0.5, 1, 1.5, 2].map(speed => (
                                        <button 
                                            key={speed} 
                                            className={`btn btn-sm px-3 py-1 ${sweepSpeed === speed ? 'btn-secondary fw-bold border-secondary' : 'btn-outline-secondary'}`}
                                            onClick={() => setSweepSpeed(speed)}
                                        >
                                            {speed}
                                        </button>
                                    ))}
                                </div>

                                {/* Custom speed input */}
                                <div className="d-flex align-items-center gap-2" style={{ width: '240px', fontSize: '0.85rem' }}>
                                    <span className="text-muted">Segundos:</span>
                                    <input 
                                        type="number" step="0.1" min="0.2" max="10" 
                                        className="form-control form-control-sm text-center border-secondary border-opacity-50 py-0" 
                                        value={sweepSpeed} onChange={(e) => setSweepSpeed(parseFloat(e.target.value) || 1)}
                                        style={{ height: '26px' }}
                                    />
                                </div>

                                {/* Botón Pulso Global */}
                                <div className="d-flex gap-2 align-items-center mt-1" style={{ width: '240px' }}>
                                    <input type="color" className="form-control-color border-0 p-0" value={themeColor} onChange={(e) => setThemeColor(e.target.value)} style={{ width: '38px', height: '34px', cursor: 'pointer', borderRadius: '6px' }} />
                                    <button 
                                        className="btn btn-outline-info flex-fill fw-bold py-2 rounded-3 select-none"
                                        onMouseDown={handlePulseGlobalStart}
                                        onMouseUp={handlePulseGlobalStop}
                                        onMouseLeave={handlePulseGlobalStop}
                                        onTouchStart={handlePulseGlobalStart}
                                        onTouchEnd={handlePulseGlobalStop}
                                    >
                                        Luces por pulso
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BARRA DE HERRAMIENTAS: BÚSQUEDA, FILTRADO Y ORDEN DE ZONAS */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3 bg-opacity-40 p-3 rounded">
                    <div className="d-flex flex-fill align-items-center" style={{ maxWidth: '350px' }}>
                        <i className="bi bi-search text-muted me-2"></i>
                        <input 
                            type="text" className="form-control form-control-sm" 
                            placeholder="Buscar zona..." value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        <div className="d-flex align-items-center">
                            <span className="small text-muted me-2"><i className="bi bi-funnel"></i> Filtrar:</span>
                            <select className="form-select form-select-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ width: '130px' }}>
                                <option value="all">Todas</option>
                                <option value="active">Encendidas</option>
                                <option value="inactive">Apagadas</option>
                            </select>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="small text-muted me-2"><i className="bi bi-sort-alpha-down"></i> Ordenar:</span>
                            <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ width: '140px' }}>
                                <option value="name">Nombre (A-Z)</option>
                                <option value="connections">Dispositivos</option>
                                <option value="status">Estado Show</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* LISTADO HORIZONTAL DE TARJETAS DE ZONA */}
                <div className="mb-4">
                    <h5 className="fw-bold mb-3"><i className="bi bi-grid-3x3-gap-fill me-1"></i> Tarjetas de Zonas ({processedZones.length})</h5>
                    <div className="d-flex flex-row overflow-auto pb-3 gap-3" style={{ scrollbarWidth: 'thin', scrollBehavior: 'smooth' }}>
                        {processedZones.length > 0 ? (
                            processedZones.map(zone => {
                                const currentPresetId = zoneSelectedPreset[zone.zoneInternal] || '';
                                return (
                                    <div key={zone.zoneInternal} className="card flex-shrink-0 border" style={getCardStyle(zone)}>
                                        <div className="card-body p-3 d-flex flex-column justify-content-between h-100">
                                            {/* Título y Estatus */}
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <h6 className="fw-bold mb-0 text-truncate" style={{ maxWidth: '170px' }}>{zone.displayName}</h6>
                                                    <span className="text-muted small font-monospace" style={{ fontSize: '0.75rem' }}>{zone.zoneInternal}</span>
                                                </div>
                                                <span className={`badge ${zone.isRunning ? 'bg-warning text-dark' : 'bg-secondary'} d-flex align-items-center`}>
                                                    {zone.isRunning ? (
                                                        <>
                                                            <span className="spinner-grow spinner-grow-sm me-1 text-danger" role="status" style={{ width: '10px', height: '10px' }}></span>
                                                            ENCENDIDO
                                                        </>
                                                    ) : 'APAGADO'}
                                                </span>
                                            </div>

                                            {/* Info de Dispositivos */}
                                            <div className="d-flex justify-content-between bg-opacity-30 p-2 border rounded mb-3 text-center" style={{ fontSize: '0.8rem' }}>
                                                <div>
                                                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Activos</span>
                                                    <span className="fw-bold text-success">{zone.active}</span>
                                                </div>
                                                <div>
                                                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Salieron</span>
                                                    <span className="text-danger">{zone.left}</span>
                                                </div>
                                                <div>
                                                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Total</span>
                                                    <span className="fw-bold text-info">{zone.total}</span>
                                                </div>
                                            </div>

                                            {/* Info de preset activo en arena */}
                                            <div className="small mb-3 border-top border-bottom border-secondary border-opacity-30 py-2" style={{ fontSize: '0.8rem' }}>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="text-muted">Preset activo:</span>
                                                    <span className="fw-bold text-warning">{zone.activePreset}</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted">Color actual:</span>
                                                    {zone.activeColor !== 'N/A' ? (
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle border border-secondary" style={{ width: '12px', height: '12px', backgroundColor: zone.activeColor, marginRight: '4px' }}></div>
                                                            <span className="font-monospace fw-bold">{zone.activeColor}</span>
                                                        </div>
                                                    ) : 'N/A'}
                                                </div>
                                            </div>

                                            {/* Selector de Preset */}
                                            <div className="mb-3">
                                                <label className="small text-muted fw-bold d-block mb-1">Preset a ejecutar:</label>
                                                <select 
                                                    className="form-select form-select-sm border-secondary border-opacity-40" 
                                                    value={currentPresetId} 
                                                    onChange={(e) => setZoneSelectedPreset({ ...zoneSelectedPreset, [zone.zoneInternal]: parseInt(e.target.value) || '' })}
                                                >
                                                    <option value="">(Configuración Global)</option>
                                                    {presetsDb.map(p => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Controles de Acción por Zona (3 BOTONES DE REQUERIMIENTO) */}
                                            <div className="d-flex flex-column gap-2 mt-auto">
                                                {/* Iniciar Solo y Exclusivo */}
                                                <div className="row g-1">
                                                    <div className="col-6">
                                                        <button 
                                                            className="btn btn-sm btn-outline-success w-100 fw-bold"
                                                            onClick={() => handleStartZoneNonExclusive(zone.zoneInternal)}
                                                            title="Iniciar esta zona manteniendo otras activas"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            Encender esta zona sin apagar lo demás
                                                        </button>
                                                    </div>
                                                    <div className="col-6">
                                                        <button 
                                                            className="btn btn-sm btn-success w-100 fw-bold"
                                                            onClick={() => handleStartZoneExclusive(zone.zoneInternal)}
                                                            title="Apagar las demás y activar solo esta zona"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            Apagar todo y encender solo esta zona
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Detener Zona */}
                                                <button 
                                                    className="btn btn-sm btn-outline-danger w-100 fw-bold"
                                                    onClick={() => handleStopZone(zone.zoneInternal)}
                                                >
                                                    <i className="bi bi-stop-fill"></i> Detener Zona
                                                </button>
                                                
                                                {/* Pulso de Zona */}
                                                <div className="d-flex gap-1 align-items-center mt-1">
                                                    <input 
                                                        type="color" className="form-control-color border-0 p-0" 
                                                        value={zonePulseColor[zone.zoneInternal] || '#FFFFFF'} 
                                                        onChange={(e) => setZonePulseColor({ ...zonePulseColor, [zone.zoneInternal]: e.target.value })} 
                                                        style={{ width: '28px', height: '24px', cursor: 'pointer', borderRadius: '4px' }} 
                                                    />
                                                    <button 
                                                        className="btn btn-sm btn-warning flex-fill fw-bold py-1 select-none"
                                                        onMouseDown={() => handlePulseZoneStart(zone.zoneInternal)}
                                                        onMouseUp={() => handlePulseZoneStop(zone.zoneInternal)}
                                                        onMouseLeave={() => handlePulseZoneStop(zone.zoneInternal)}
                                                        onTouchStart={() => handlePulseZoneStart(zone.zoneInternal)}
                                                        onTouchEnd={() => handlePulseZoneStop(zone.zoneInternal)}
                                                    >
                                                        Ráfaga Zona
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="w-100 text-center p-5 text-muted border border-secondary border-dashed rounded">
                                No se encontraron zonas.
                            </div>
                        )}
                    </div>
                </div>

                {/* RESUMEN DE CONEXIONES POR ZONA */}
                <div className='mt-4 pt-3 border-top border-secondary'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0"><i className="bi bi-bar-chart-line-fill me-1"></i> Resumen de Conexiones por Zona</h5>
                        {liveStats && <span className="badge bg-secondary">Ronda Activa: {liveStats.roundName}</span>}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle text-center" style={{ fontSize: '0.9rem' }}>
                            <thead className="table-active">
                                <tr>
                                    <th>Nombre Zona</th>
                                    {/* <th>Id de Zona</th> */}
                                    <th>Dispositivos Conectados</th>
                                    <th>Total Salieron</th>
                                    <th>Histórico Registros</th>
                                    <th>Estado del Show</th>
                                    <th>Animación Corriendo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mergedZoneStats.length > 0 ? (
                                    mergedZoneStats.map(stat => (
                                        <tr key={stat.zoneInternal}>
                                            <td className="fw-bold text-start ps-3">{stat.displayName}</td>
                                            {/* <td className="font-monospace text-muted">{stat.zoneInternal}</td> */}
                                            <td>
                                                <span className="badge bg-success fs-6 px-3">{stat.active}</span>
                                            </td>
                                            <td className="text-muted">{stat.left}</td>
                                            <td className="fw-bold">{stat.total}</td>
                                            <td>
                                                <span className={`badge ${stat.isRunning ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                    {stat.isRunning ? 'ENCENDIDO' : 'Apagado'}
                                                </span>
                                            </td>
                                            <td>
                                                {stat.isRunning ? (
                                                    <span className="small text-warning">
                                                        {stat.activePreset} ({stat.activeColor})
                                                    </span>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center p-3 text-muted">
                                            Cargando auditoría...
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </section>

            {/* ==========================================
                MODAL DEL DISEÑADOR DE PRESETS (TOTALMENTE OPACO)
            ========================================== */}
            {isPresetModalOpen && (
                <div className="modal show d-flex align-items-center justify-content-center p-3" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1050 }}>
                    <div className="card border-secondary" style={{ width: '100%', maxWidth: '650px', borderRadius: '20px', backgroundColor: '#fff', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                        <div className="card-header p-3 border-secondary d-flex justify-content-between align-items-center" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                            <span className="fw-bold fs-5 text-info"><i className="bi bi-palette me-2"></i> Diseñador de Presets</span>
                            <button className="btn-close" onClick={() => setIsPresetModalOpen(false)}></button>
                        </div>
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <span className="text-muted small fw-bold d-block">PRESET SELECCIONADO</span>
                                    {isLoadingPresets ? (
                                        <span className="small text-muted">Cargando...</span>
                                    ) : (
                                        <select className="form-select form-select-sm w-auto" value={activePreset?.id || ''} onChange={(e) => applyPresetToUI(presetsDb.find(p => p.id === parseInt(e.target.value)))}>
                                            <option value="" disabled>-- Selecciona un Preset --</option>
                                            {presetsDb.map(p => (
                                                <option key={p.id} value={p.id}>{p.name} {p.isSystemPreset ? '(Sistema)' : ''}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div className="d-flex align-items-center">
                                    <label className="mb-0 me-2 small fw-bold">Ritmo (BPM):</label>
                                    <input 
                                        type="number" className="form-control form-control-sm text-center fw-bold" 
                                        style={{ width: '70px' }} value={beatsPerMinute} 
                                        onChange={(e) => setBeatsPerMinute(parseInt(e.target.value) || 0)} 
                                    />
                                </div>
                            </div>

                            {/* LISTADO DE FRAMES */}
                            <div className="border border-secondary border-opacity-40 rounded mb-3 bg-black bg-opacity-40" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover mb-0 align-middle text-center border-0">
                                    <thead>
                                        <tr className="table-active">
                                            <th style={{ width: '70px' }}>Orden</th>
                                            <th>Color Pantalla</th>
                                            <th>Luz Flash</th>
                                            <th style={{ width: '60px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {animationFrames.map((frame, index) => (
                                            <tr key={frame.id}>
                                                <td>
                                                    <div className="d-flex flex-column align-items-center">
                                                        <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameUp(index)} disabled={index === 0}><i className="bi bi-caret-up-fill text-info"></i></button>
                                                        <span className="fw-bold small">{index + 1}</span>
                                                        <button className="btn btn-sm text-muted p-0 border-0" onClick={() => moveFrameDown(index)} disabled={index === animationFrames.length - 1}><i className="bi bi-caret-down-fill text-info"></i></button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <input 
                                                            type="color" className="form-control form-control-color border-0 p-0 me-2" 
                                                            value={frame.color} onChange={(e) => updateFrame(frame.id, 'color', e.target.value)} 
                                                            style={{ width: '35px', height: '28px', cursor: 'pointer' }} 
                                                        />
                                                        <span className="small text-muted font-monospace">{frame.color.toUpperCase()}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="form-check form-switch d-flex justify-content-center">
                                                        <input 
                                                            className="form-check-input" type="checkbox" checked={frame.flashOn} 
                                                            onChange={(e) => updateFrame(frame.id, 'flashOn', e.target.checked)} 
                                                            style={{ cursor: 'pointer', width: '38px', height: '18px' }} 
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeFrame(frame.id)}>
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <button className="btn btn-sm btn-success fw-bold" onClick={addFrame}>
                                    <i className="bi bi-plus-circle me-1"></i> Agregar Frame
                                </button>
                                <div className="d-flex gap-2">
                                    {hasUnsavedChanges && activePreset && (
                                        <button className="btn btn-sm btn-outline-primary" onClick={handleSaveUpdate}>
                                            <i className="bi bi-floppy-fill me-1"></i> Guardar Cambios
                                        </button>
                                    )}
                                    {hasUnsavedChanges && activePreset && (
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => triggerModal(`¿Descartar cambios de "${activePreset.name}"?`, handleRestoreDefault, 'Sí, descartar')}>
                                            Descartar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card-footer border-secondary p-3 d-flex justify-content-between align-items-center" style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}>
                            <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-secondary fw-bold" onClick={handleSaveAsNew}>
                                    <i className="bi bi-file-earmark-plus me-1 text-info"></i> Nuevo como...
                                </button>
                                {activePreset && !activePreset.isSystemPreset && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => triggerModal(`¿Eliminar preset "${activePreset.name}"?`, handleDeletePreset, 'Eliminar')}>
                                        <i className="bi bi-trash"></i> Eliminar
                                    </button>
                                )}
                            </div>
                            <button className="btn btn-primary fw-bold px-4" onClick={() => setIsPresetModalOpen(false)}>
                                Listo / Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==========================================
                MODAL DE CONFIGURACIÓN DE ORDEN DE OLA
            ========================================== */}
            {isOrderModalOpen && (
                <div className="modal show d-flex align-items-center justify-content-center p-3" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1050 }}>
                    <div className="card border-secondary" style={{ width: '100%', maxWidth: '500px', borderRadius: '20px', backgroundColor: '#fff', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                        <div className="card-header p-3 border-secondary d-flex justify-content-between align-items-center" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                            <span className="fw-bold fs-5 text-info"><i className="bi bi-sliders me-2"></i> Orden de la Oleada</span>
                            <button className="btn-close" onClick={() => setIsOrderModalOpen(false)}></button>
                        </div>
                        <div className="card-body p-3">
                            <p className="small text-muted mb-3">
                                Ordena las zonas usando las flechas. El barrido (ola) recorrerá únicamente las zonas que tengan al menos 1 dispositivo activo, siguiendo este orden.
                            </p>
                            
                            <ul className="list-group mb-3" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                {zoneOrder.map((zoneName, index) => {
                                    const zone = stadiumZones.find(z => z.internalName === zoneName);
                                    const zoneStat = mergedZoneStats.find(z => z.zoneInternal === zoneName);
                                    const isActive = zoneStat && zoneStat.active > 0;
                                    
                                    return (
                                        <li key={zoneName} className="list-group-item d-flex justify-content-between align-items-center py-2.5">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="badge bg-secondary rounded-pill d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>{index + 1}</span>
                                                <div className="text-start">
                                                    <span className="fw-semibold d-block text-dark">{zone ? zone.displayName : zoneName}</span>
                                                    <span className={`small ${isActive ? 'text-success fw-bold' : 'text-muted'}`}>
                                                        {isActive ? (
                                                            <span><i className="bi bi-people-fill me-1"></i> {zoneStat.active} activos</span>
                                                        ) : (
                                                            <span>Sin dispositivos</span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="d-flex gap-1">
                                                <button 
                                                    className="btn btn-sm btn-outline-secondary px-2 py-1"
                                                    onClick={() => {
                                                        if (index === 0) return;
                                                        const newOrder = [...zoneOrder];
                                                        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                                        setZoneOrder(newOrder);
                                                    }}
                                                    disabled={index === 0}
                                                >
                                                    <i className="bi bi-arrow-up"></i>
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-secondary px-2 py-1"
                                                    onClick={() => {
                                                        if (index === zoneOrder.length - 1) return;
                                                        const newOrder = [...zoneOrder];
                                                        [newOrder[index + 1], newOrder[index]] = [newOrder[index], newOrder[index + 1]];
                                                        setZoneOrder(newOrder);
                                                    }}
                                                    disabled={index === zoneOrder.length - 1}
                                                >
                                                    <i className="bi bi-arrow-down"></i>
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            
                            <div className="text-end">
                                <button className="btn btn-primary fw-bold px-4" onClick={() => setIsOrderModalOpen(false)}>
                                    Listo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMACIÓN */}
            {showConfirmModal && (
                <Confirm 
                    message={confirmModalData?.message} 
                    actionToYesButton={confirmModalData?.actionToYesButton} 
                    textYesButton={confirmModalData?.textYesButton} 
                    textNoButton={confirmModalData?.textNoButton} 
                    setShowModal={setShowConfirmModal} 
                    setConfirmData={setConfirmModalData} 
                />
            )}
        </SecondaryBase>
    );
};

export { LightShow };